"use client";

import { c as _c } from "react/compiler-runtime";
import type { FC } from "react";
import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { formatNumberWithCommas } from "@/helpers/Helpers";
import { TrophyIcon, ChevronDownIcon } from "@heroicons/react/24/outline";
import type { ApiWaveOutcome } from "@/generated/models/ApiWaveOutcome";
import type { WaveOutcomeDistributionState } from "@/types/waves.types";
interface WaveManualOutcomeProps {
  readonly outcome: ApiWaveOutcome;
  readonly distribution: WaveOutcomeDistributionState;
}
const DEFAULT_AMOUNTS_TO_SHOW = 3;
export const WaveManualOutcome: FC<WaveManualOutcomeProps> = t0 => {
  const $ = _c(47);
  const {
    outcome,
    distribution
  } = t0;
  const [isOpen, setIsOpen] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const {
    items,
    totalCount,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    isLoading,
    isError,
    errorMessage
  } = distribution;
  const winnersCount = totalCount;
  let t1;
  if ($[0] !== items || $[1] !== showAll) {
    t1 = showAll ? items : items.slice(0, DEFAULT_AMOUNTS_TO_SHOW);
    $[0] = items;
    $[1] = showAll;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const visibleItems = t1;
  const remainingCount = Math.max(totalCount - visibleItems.length, 0);
  const shouldShowMore = hasNextPage || !showAll && items.length > DEFAULT_AMOUNTS_TO_SHOW;
  let t2;
  if ($[3] !== fetchNextPage || $[4] !== hasNextPage || $[5] !== isFetchingNextPage || $[6] !== showAll) {
    t2 = () => {
      if (!showAll) {
        setShowAll(true);
      }
      if (hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    };
    $[3] = fetchNextPage;
    $[4] = hasNextPage;
    $[5] = isFetchingNextPage;
    $[6] = showAll;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const onViewMore = t2;
  let t3;
  if ($[8] !== isOpen) {
    t3 = () => setIsOpen(!isOpen);
    $[8] = isOpen;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <div className="tw-flex tw-items-center tw-justify-center tw-size-10 tw-rounded-lg tw-bg-[#E8D48A]/10"><TrophyIcon className="tw-size-5 tw-text-[#E8D48A]" /></div>;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <div className="tw-text-sm tw-font-medium tw-text-iron-200">Manual</div>;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== isLoading || $[13] !== winnersCount) {
    t6 = <div className="tw-flex tw-items-center tw-gap-3">{t4}<div className="tw-text-left">{t5}<div className="tw-text-xs tw-text-iron-500">{isLoading ? <span className="tw-animate-pulse tw-bg-iron-800 tw-h-3 tw-w-16 tw-rounded tw-inline-block" /> : <>{formatNumberWithCommas(winnersCount)}{" "}{winnersCount === 1 ? "winner" : "winners"}</>}</div></div></div>;
    $[12] = isLoading;
    $[13] = winnersCount;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const t7 = isOpen ? 180 : 0;
  let t8;
  if ($[15] !== t7) {
    t8 = {
      rotate: t7
    };
    $[15] = t7;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t10;
  let t9;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = {
      duration: 0.2
    };
    t10 = <ChevronDownIcon className="tw-size-4 tw-text-iron-500" />;
    $[17] = t10;
    $[18] = t9;
  } else {
    t10 = $[17];
    t9 = $[18];
  }
  let t11;
  if ($[19] !== t8) {
    t11 = <motion.div animate={t8} transition={t9}>{t10}</motion.div>;
    $[19] = t8;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] !== t11 || $[22] !== t6) {
    t12 = <div className="tw-flex tw-items-center tw-justify-between">{t6}{t11}</div>;
    $[21] = t11;
    $[22] = t6;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  let t13;
  if ($[24] !== outcome.description) {
    t13 = outcome.description && <div className="tw-mt-3 tw-pt-3 tw-border-t tw-border-iron-800/50 tw-border-solid tw-border-x-0 tw-border-b-0"><div className="tw-flex tw-items-center tw-justify-between"><span className="tw-text-xs tw-text-iron-500">Description</span><span className="tw-text-xs tw-text-iron-300 tw-text-right tw-max-w-[200px] tw-truncate">{outcome.description}</span></div></div>;
    $[24] = outcome.description;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  let t14;
  if ($[26] !== isOpen || $[27] !== t12 || $[28] !== t13 || $[29] !== t3) {
    t14 = <button type="button" aria-expanded={isOpen} onClick={t3} className="tw-w-full tw-border-0 tw-px-4 tw-py-4 tw-bg-transparent tw-cursor-pointer">{t12}{t13}</button>;
    $[26] = isOpen;
    $[27] = t12;
    $[28] = t13;
    $[29] = t3;
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  let t15;
  if ($[31] !== errorMessage || $[32] !== isError || $[33] !== isFetchingNextPage || $[34] !== isLoading || $[35] !== isOpen || $[36] !== items || $[37] !== onViewMore || $[38] !== remainingCount || $[39] !== shouldShowMore || $[40] !== visibleItems) {
    t15 = isOpen && <motion.div initial={{
      height: 0,
      opacity: 0
    }} animate={{
      height: "auto",
      opacity: 1
    }} exit={{
      height: 0,
      opacity: 0
    }} transition={{
      duration: 0.2
    }} className="tw-overflow-hidden"><div className="tw-border-t tw-border-iron-800/50 tw-border-solid tw-border-x-0 tw-border-b-0">{visibleItems.map(_temp)}{isLoading && <div className="tw-px-4 tw-py-3 tw-text-center tw-text-sm tw-text-iron-500">Loading winners...</div>}{isError && <div className="tw-px-4 tw-py-3 tw-text-center tw-text-sm tw-text-red-400">{errorMessage || "Failed to load winners"}</div>}{!isLoading && !isError && items.length === 0 && <div className="tw-px-4 tw-py-3 tw-text-center tw-text-sm tw-text-iron-500">No winners yet</div>}{shouldShowMore && <button className="tw-border-0 tw-w-full tw-px-4 tw-py-3 tw-text-sm tw-text-iron-500 desktop-hover:hover:tw-text-iron-300 tw-bg-transparent tw-transition-colors tw-cursor-pointer" onClick={onViewMore} disabled={isFetchingNextPage}>{isFetchingNextPage ? "Loading..." : `View ${remainingCount} more`}</button>}</div></motion.div>;
    $[31] = errorMessage;
    $[32] = isError;
    $[33] = isFetchingNextPage;
    $[34] = isLoading;
    $[35] = isOpen;
    $[36] = items;
    $[37] = onViewMore;
    $[38] = remainingCount;
    $[39] = shouldShowMore;
    $[40] = visibleItems;
    $[41] = t15;
  } else {
    t15 = $[41];
  }
  let t16;
  if ($[42] !== t15) {
    t16 = <AnimatePresence initial={false}>{t15}</AnimatePresence>;
    $[42] = t15;
    $[43] = t16;
  } else {
    t16 = $[43];
  }
  let t17;
  if ($[44] !== t14 || $[45] !== t16) {
    t17 = <div className="tw-rounded-xl tw-bg-iron-950 tw-ring-1 tw-ring-white/5 tw-transition-all tw-duration-300 desktop-hover:hover:tw-ring-white/10">{t14}{t16}</div>;
    $[44] = t14;
    $[45] = t16;
    $[46] = t17;
  } else {
    t17 = $[46];
  }
  return t17;
};
function _temp(item, i) {
  const itemLabel = item.amount === 0 ? "-" : item.description ?? "";
  return <div key={`wave-manual-outcome-${item.index}`} className="tw-px-4 tw-py-3 tw-flex tw-items-center tw-justify-between tw-transition-colors desktop-hover:hover:tw-bg-white/[0.02]"><div className="tw-flex tw-items-center tw-gap-3"><span className="tw-flex tw-items-center tw-justify-center tw-size-6 tw-rounded-md tw-bg-iron-900 tw-text-iron-400 tw-text-xs tw-font-medium">{i + 1}</span><span className="tw-text-sm tw-text-iron-200">{itemLabel}</span></div></div>;
}